// components/VideoModal.tsx
import { useState, ReactNode, useEffect } from 'react'
import { X } from 'lucide-react'
import { createPortal } from 'react-dom'

interface VideoModalProps {
  videoId: string
  className?: string
  children: ReactNode
}

export default function VideoModal({
  videoId,
  className = '',
  children,
}: VideoModalProps) {
  const [open, setOpen] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    if (open) {
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [open])

  const handleOpen = () => {
    setIsAnimating(true)
    setOpen(true)
  }

  const handleClose = () => {
    setIsAnimating(false)
    setOpen(false)
  }

  if (!mounted) return null

  return (
    <>
      <button
        onClick={handleOpen}
        className={`${className} transition-transform hover:scale-105`}
      >
        {children}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          onClick={handleClose}
        >
          {/* Backdrop with blur and fade animation */}
          <div 
            className={`absolute inset-0 bg-black/80 transition-opacity duration-300 ${
              isAnimating ? 'opacity-100' : 'opacity-0'
            } backdrop-blur-[6px]`}
          />
          
          {/* Modal content with scale animation */}
          <div
            className={`relative w-[90vw] h-[80vh] max-w-4xl max-h-[700px] rounded-3xl overflow-hidden shadow-2xl shadow-black/60 bg-[#18192a] transform transition-all duration-300 ${
              isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            }`}
            onClick={e => e.stopPropagation()}
            style={{ boxShadow: '0 8px 48px 0 rgba(0,0,0,0.45)' }}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 transition-colors shadow-lg"
            >
              <X className="h-6 w-6 text-white" />
            </button>

            {/* Video iframe */}
            <iframe
              className="w-full h-full rounded-2xl"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
              title="TL;DR Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ background: '#18192a' }}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
